// composables/useItemIssuePresentation.js
//
// The SINGLE classifier for how an order-box item's issue state is presented to
// the MEMBER. It unifies the two historically-separate signals into one:
//
//   • the typed member-answerable issue signal — `item.activeIssue`
//     (`{ id, reason, status, awaitingMember, allowedResponses }`, flag-gated),
//   • the item's own `ItemStatus` — the base signal that is always present, even
//     when the member-issues flag is OFF or the issue was raised leader-direct
//     (no `item_issues` row).
//
// Design principle: OBI status is the BASE layer; the typed `activeIssue` is
// ENRICHMENT. This makes the presentation robust to the feature flag and to
// legacy leader-direct issues — an item mid-issue always reads as a calm
// "being handled" state, and only UPGRADES to the actionable decision affordance
// when it is genuinely the member's turn (`activeIssue.awaitingMember`).
//
// Why this replaces the legacy path: the old red «مشكلة» chip + resolution sheet
// (change-supplier / wait-restock / cancel) keyed off `ItemStatus.ISSUE` /
// `OUT_OF_STOCK` and offered the member actions the backend ALWAYS rejects (an
// item is only ISSUE while in an active plan, and OrderBoxItemPolicy denies a
// member's update/cancel on an in-active-plan item — a 403 dead-end). The member
// is reactive-only: they answer a leader-sent decision, and otherwise just see
// status. This classifier encodes exactly that.
//
// Kinds (member-facing):
//   needs-decision → it's the member's turn; the row/chip deep-links to the
//                    /issues/:id/ decision card. The signal is the question
//                    header (`activeIssue.awaitingMember`), NOT the OBI status —
//                    since 2026-09-02 the item rests at ISSUE for the whole
//                    conversation, so OBI status alone can never say this.
//   being-handled  → mid-issue but NOT the member's turn: leader raised-not-sent,
//                    member already answered (ball back with the leader), or a
//                    legacy/flag-off issue. (OBI = ISSUE without an awaiting
//                    typed signal.) Readable, not ANSWERABLE — the row still
//                    opens the card, it just has no form on it.
//   out-of-stock   → terminal awareness; the item could not be collected.
//   none           → no issue presentation; render the normal status chip.
//
// READABLE ≠ ANSWERABLE. Since interaction-v2 the backend's `item-issues/{id}`
// `show` serves an issue in EVERY status, so a being-handled item can open the
// SAME card as a needs-decision one — read-only, with the leader's message and
// the full timeline. That is what closes the "I can see there's a problem but I
// can't find out what it is" gap. Whether a given presentation can actually be
// opened is `isRoutableIssue`, NOT the kind alone.
//
// WHICH ID OPENS IT (ASK 8, brief 2026-07-26-member-issue-fields-asks-2-3-7d-8):
// `activeIssue` is a HasOne of the item's NON-resolved issue, so it goes null the
// instant the leader resolves — exactly when the member most wants the
// explanation. The backend now also emits `lastIssueId`, the item's most-recent
// issue in ANY state, which stays put. So every branch below resolves its id as
// `activeIssue.id ?? lastIssueId`: identical while the issue is live (the live
// issue IS the latest), and the permanent history pointer once it is not. That
// is what makes an out-of-stock or otherwise-resolved item openable at all —
// `show` is state-agnostic for the owner, so the fetch succeeds in every state.
//
// Branch precedence below, spelt out because it decides what is routable:
// only branch 1 (AWAITING the member) can overtake an out-of-stock status —
// branch 2 is additionally gated on the OBI status being ISSUE.
// So an OOS item carrying a leader-court issue still reads (and stays) OOS,
// while an OOS item genuinely awaiting an answer still routes, which is right:
// the member is being asked something and the card is where they answer.
import { computed, unref } from 'vue';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';

export const ISSUE_PRESENTATION = {
  NEEDS_DECISION: 'needs-decision',
  BEING_HANDLED: 'being-handled',
  OUT_OF_STOCK: 'out-of-stock',
  NONE: 'none',
};

const NONE = Object.freeze({
  kind: ISSUE_PRESENTATION.NONE,
  issueId: null,
  reason: null,
  awaitingMember: false,
});

/** Normalize an id from either projection to a non-empty string, or null.
 *  An id of '' would build the path '/issues//', which misses the route and
 *  lands on the 404 catch-all — so it is treated as absent here, at the source,
 *  rather than being caught separately by every consumer. */
function normalizeId(id) {
  if (id == null) return null;
  const str = String(id);
  return str === '' ? null : str;
}

/**
 * Pure classifier — given a plain order-box-item object, return its member-facing
 * issue presentation. Safe to call in a `v-for` (no reactivity, cheap).
 *
 * @param {object|null|undefined} item
 * @returns {{ kind: string, issueId: string|null, reason: string|null, awaitingMember: boolean }}
 */
export function classifyItemIssue(item) {
  if (!item) return NONE;

  const active = item.activeIssue;
  // The permanent history pointer — see the module header. Live issue first (it
  // is the projection whose state every branch below is describing), then the
  // pointer that survives its resolution.
  const historyId = normalizeId(item.lastIssueId);
  const readableId = normalizeId(active?.id) ?? historyId;

  // 1. Member's turn — a typed issue was sent to THIS member. The only actionable
  //    state; requires a present id (the card is reached by it), so an
  //    awaiting-but-idless payload degrades to "being handled" below, never a
  //    dead affordance.
  if (active && active.awaitingMember && active.id != null) {
    return {
      kind: ISSUE_PRESENTATION.NEEDS_DECISION,
      issueId: String(active.id),
      reason: active.reason ?? null,
      awaitingMember: true,
    };
  }

  const status = Number(item.status);

  // 2. Being handled — the item is mid-issue but the ball is not in the member's
  //    court (raised-not-sent, already-answered, legacy leader-direct, or
  //    flag-off). ISSUE is the ONLY status this branch keys on: since
  //    2026-09-02 the item rests at ISSUE for the whole conversation — ask →
  //    send → answer → revision → close — so a live "being handled" item is
  //    never actually at AWAITING_APPROVAL any more. A row still resting at 32
  //    is a straggler falling through to branch 4/NONE below, which is correct:
  //    it reads its calm status chip until the nightly reconcile walks it to 31.
  if (status === ItemStatus.ISSUE) {
    return {
      kind: ISSUE_PRESENTATION.BEING_HANDLED,
      issueId: readableId,
      reason: active?.reason ?? null,
      awaitingMember: false,
    };
  }

  // 3. Out of stock — terminal awareness (a reservation release; the wallet
  //    OUT_OF_STOCK_REFUND row is that release, not a refund of a real charge).
  //    Now ROUTABLE when the item carries issue history: marking an item OOS
  //    RESOLVES its issue (the `marked-out-of-stock` terminal timeline step),
  //    which nulls `activeIssue` — `lastIssueId` is what survives that and lets
  //    the member read WHY, which is the moment they most want to. Still not
  //    routable without one: an item can reach OOS with no `item_issues` row at
  //    all (leader-direct, or the feature flag off), and there is nothing to open.
  if (status === ItemStatus.OUT_OF_STOCK) {
    return {
      kind: ISSUE_PRESENTATION.OUT_OF_STOCK,
      issueId: readableId,
      reason: null,
      awaitingMember: false,
    };
  }

  // 4. No issue PRESENTATION — but possibly issue HISTORY. An item whose issue
  //    was sorted out is back to an ordinary status and must read as one (its
  //    normal status chip, its ordinary details sheet), so this stays NONE and
  //    stays non-routable: hijacking the row of a line that looks and is fine
  //    would be a worse regression than the dead end it fixes. The id is carried
  //    anyway so the details sheet can offer "what happened here" explicitly —
  //    see `hasReadableIssue`.
  if (historyId) {
    return {
      kind: ISSUE_PRESENTATION.NONE,
      issueId: historyId,
      reason: null,
      awaitingMember: false,
    };
  }

  return NONE;
}

// The kinds whose ROW itself opens the issue card. NONE is deliberately absent
// even when it carries a history id — see branch 4 of the classifier.
const ROUTABLE_KINDS = [
  ISSUE_PRESENTATION.NEEDS_DECISION,
  ISSUE_PRESENTATION.BEING_HANDLED,
  ISSUE_PRESENTATION.OUT_OF_STOCK,
];

/**
 * Is there an issue detail to open AT ALL for this item — in any state? The
 * broad predicate, for surfaces that offer an explicit "what happened here"
 * affordance rather than taking over the row (the details sheet).
 *
 * Re-guards the empty string rather than trusting `normalizeId` upstream: this
 * is a public predicate and callers legitimately hand-build a presentation. An
 * id of '' builds the path '/issues//', which misses the route and lands on the
 * 404 catch-all — cheaper to refuse the affordance than to send the member
 * somewhere that cannot explain itself.
 *
 * @param {{ issueId: string|null }|null|undefined} presentation
 * @returns {boolean}
 */
export function hasReadableIssue(presentation) {
  return normalizeId(presentation?.issueId) != null;
}

/**
 * Can this presentation open the /issues/:id/ card FROM ITS ROW? The ONE
 * predicate every list surface asks before wiring a tap — so the order rows and
 * the totals breakdown can never disagree about which items are openable.
 *
 * True for needs-decision, and for a being-handled or out-of-stock item that has
 * a typed issue behind it (live via `activeIssue`, or resolved via
 * `lastIssueId`). False when:
 *   • the member-issues feature flag is OFF (both keys are omitted from the
 *     resource entirely — see OrderBoxItemResource::attributes),
 *   • the issue was raised leader-direct with no `item_issues` row (legacy),
 *   • the line is ordinary — including one whose issue was sorted out, which
 *     keeps its normal row behaviour and offers the detail from its sheet.
 * In the first two the item still SHOWS its calm pill; it just doesn't navigate,
 * and the row falls back to the ordinary details sheet.
 *
 * @param {{ kind: string, issueId: string|null }|null|undefined} presentation
 * @returns {boolean}
 */
export function isRoutableIssue(presentation) {
  if (!presentation) return false;
  if (!ROUTABLE_KINDS.includes(presentation.kind)) return false;
  return hasReadableIssue(presentation);
}

/**
 * Which DecisionChip variant an item should render, or null when it must not
 * render one at all. Derived from isRoutableIssue rather than from `kind`, so
 * "do we show a chip" and "which court does it announce" can never disagree —
 * a normal line must never be labelled as the leader's court, and an
 * out-of-stock line must keep announcing itself as out of stock even now that
 * it opens something.
 *
 * @param {{ kind: string, issueId: string|null }|null|undefined} presentation
 * @returns {'member'|'handled'|'oos'|null}
 */
export function issueChipVariant(presentation) {
  if (!isRoutableIssue(presentation)) return null;
  if (presentation.kind === ISSUE_PRESENTATION.NEEDS_DECISION) return 'member';
  if (presentation.kind === ISSUE_PRESENTATION.OUT_OF_STOCK) return 'oos';
  return 'handled';
}

/**
 * Reactive wrapper for single-item component use (mirrors useItemStatus's
 * getter-or-ref source signature). Returns a computed `presentation`.
 *
 * @param {(() => object)|import('vue').Ref<object>|object} itemSource
 */
export function useItemIssuePresentation(itemSource) {
  const item = computed(() =>
    typeof itemSource === 'function' ? itemSource() : unref(itemSource),
  );

  const presentation = computed(() => classifyItemIssue(item.value));

  return { presentation };
}
